/**
 * Helpers for keeping the parallel arrays on `DraftSettings`
 * (`referenceImages` ↔ `referenceImageAssetIds` ↔ `referenceImageNames`)
 * aligned when a caller mutates `referenceImages`.
 *
 * `alignParallelArrayByUrl` walks `nextRefs` (the post-mutation URL list) and
 * picks each value from the position in `currentValues` whose corresponding
 * URL in `currentRefs` matches. The `usedIndices` guard prevents two duplicate
 * URLs in `nextRefs` from picking the same source slot — duplicates consume
 * distinct source positions in left-to-right order so that asset IDs and
 * names follow URL identity through a reorder, not URL-collapse.
 *
 * `normalizeLength` is the simpler escape hatch when the caller supplies
 * their own asset-id or name mutator: the returned array is padded to
 * `nextRefs.length` with `fallback`, but ordering matches the caller's
 * mutator output position-for-position rather than by URL.
 */

export function alignParallelArrayByUrl<T>(
  currentRefs: readonly string[],
  currentValues: readonly T[],
  nextRefs: readonly string[],
  fallback: T,
): T[] {
  const usedIndices = new Set<number>()
  return nextRefs.map(nextRef => {
    const matchIndex = currentRefs.findIndex((ref, index) => !usedIndices.has(index) && ref === nextRef)
    if (matchIndex === -1) return fallback
    usedIndices.add(matchIndex)
    return currentValues[matchIndex] ?? fallback
  })
}

export function normalizeLength<T>(values: readonly T[], length: number, fallback: T): T[] {
  return Array.from({ length }, (_, index) => values[index] ?? fallback)
}
